// Pure route resolution for the SPA, kept free of DOM/React/imports so it can be unit tested directly
// (scripts/portal-routing.test.mjs).
//
// Contact portal hosts (contact[-int|-npe|-ppe].microsoft.com — see contactPortal.ts):
//   - /en-us/teams while peridot.teams.enabled is OFF -> redirects to TEAMS_DISABLED_FALLBACK_URL,
//                                        regardless of contactportal.home.enabled. This is the ONLY case that
//                                        uses that URL — every other dead end below uses CONTACT_PORTAL_FALLBACK_URL.
//   - contactportal.home.enabled OFF  -> every other URL redirects to CONTACT_PORTAL_FALLBACK_URL.
//   - contactportal.home.enabled ON   -> ONLY /en-us/teams (peridot.teams.enabled) and /en-us/rewards
//                                        (peridot.rewards.enabled) are served; every other URL — including a page
//                                        whose flag is off — redirects to CONTACT_PORTAL_FALLBACK_URL.
// Every other host (AFD endpoints, App Service hosts, localhost) keeps the full SPA: home, #/settings, #/admin,
// Teams (flag-gated, en-us only — other Teams URLs redirect to /en-us/teams) and Rewards (flag-gated, any locale,
// clean path or #/rewards hash).

export type PortalRoute = 'home' | 'settings' | 'teams' | 'rewards' | 'admin';

export type RouteDecision =
  | { kind: 'render'; route: PortalRoute }
  | { kind: 'redirect'; url: string };

export interface PortalRoutingFlags {
  contactPortalHomeEnabled: boolean;
  teamsEnabled: boolean;
  rewardsEnabled: boolean;
}

export interface RouteInput {
  origin: string;
  pathname: string;
  hash: string;
  contactPortalHost: boolean;
  flags: PortalRoutingFlags;
}

export const CONTACT_PORTAL_FALLBACK_URL = 'https://support.microsoft.com/en-us/home/contactus';

// Where /en-us/teams redirects to on the contact portal hosts specifically when peridot.teams.enabled is
// OFF — not a general-purpose fallback; every other dead end still uses CONTACT_PORTAL_FALLBACK_URL above.
export const TEAMS_DISABLED_FALLBACK_URL =
  'https://occclientglobal.iconchannelserviceprod.microsoft.com/?organizationid=a8c967f2-d0c0-4bfc-a40f-6c7f603fb3b8&partnercode=teams&locale=en-us';

// Teams for Life is en-us-only, so Teams is served ONLY at this clean path.
export const TEAMS_CLEAN_PATH = '/en-us/teams';
// The only Rewards URL served on the contact portal hosts.
export const REWARDS_CLEAN_PATH = '/en-us/rewards';

const TEAMS_HASH = '#/teams';
const REWARDS_HASH = '#/rewards';
// Any /<locale>/teams or bare /teams clean path ("Teams intent" to redirect to TEAMS_CLEAN_PATH off the contact hosts).
const TEAMS_PATH_RE = /^\/(?:[a-z]{2}-[a-z]{2}\/)?teams\/?$/i;
// Any /<locale>/rewards or bare /rewards clean path (served off the contact hosts).
const REWARDS_PATH_RE = /^\/(?:[a-z]{2}-[a-z]{2}\/)?rewards\/?$/i;

/** Path without a trailing slash, lower-cased. */
export function normalizePath(pathname: string): string {
  return pathname.replace(/\/+$/, '').toLowerCase() || '/';
}

const render = (route: PortalRoute): RouteDecision => ({ kind: 'render', route });
const redirect = (url: string): RouteDecision => ({ kind: 'redirect', url });

export function resolveRoute(input: RouteInput): RouteDecision {
  const { flags } = input;
  const path = normalizePath(input.pathname);

  if (input.contactPortalHost) {
    // Teams specifically being unavailable (its own flag off) always goes to TEAMS_DISABLED_FALLBACK_URL,
    // independent of contactportal.home.enabled — this is the only dead end that uses that URL.
    if (path === TEAMS_CLEAN_PATH && !flags.teamsEnabled) {
      return redirect(TEAMS_DISABLED_FALLBACK_URL);
    }
    if (flags.contactPortalHomeEnabled) {
      if (flags.teamsEnabled && path === TEAMS_CLEAN_PATH) return render('teams');
      if (flags.rewardsEnabled && path === REWARDS_CLEAN_PATH) return render('rewards');
    }
    return redirect(CONTACT_PORTAL_FALLBACK_URL);
  }

  if (input.hash.startsWith('#/admin')) return render('admin');
  if (input.hash === '#/settings') return render('settings');
  if (flags.teamsEnabled) {
    if (path === TEAMS_CLEAN_PATH) return render('teams');
    if (TEAMS_PATH_RE.test(input.pathname) || input.hash === TEAMS_HASH) {
      return redirect(`${input.origin}${TEAMS_CLEAN_PATH}`);
    }
  }
  if (flags.rewardsEnabled && (input.hash === REWARDS_HASH || REWARDS_PATH_RE.test(input.pathname))) {
    return render('rewards');
  }
  return render('home');
}
